(function () {
    'use strict';

    angular
        .module('elogbooks.admin.site-associations')
        .controller('SiteApproverUserController', SiteApproverUserController);

    SiteApproverUserController.$inject = [
        'userResponse',
        'siteCollectionLink',
        'modulesService',
        'apiClient',
        'messenger',
        '$rootScope',
        '$timeout',
        '$translate',
        '$window',
        'siteAccessConfirmationModal'
    ];

    function SiteApproverUserController(
        userResponse,
        siteCollectionLink,
        modulesService,
        apiClient,
        messenger,
        $rootScope,
        $timeout,
        $translate,
        $window,
        siteAccessConfirmationModal
    ) {
        const vm = this;

        vm.user = userResponse;
        vm.approvalTypes = buildApprovalTypes();
        vm.toggleApprovalType = toggleApprovalType;
        vm.initApprovalType = initApprovalType;
        vm.cancel = cancel;
        vm.update = update;

        function toggleApprovalType(approvalType) {
            approvalType.expanded = !approvalType.expanded;
        }

        function initApprovalType(approvalType) {
            if (approvalType.siteSelect) {
                return;
            }

            approvalType.uniqueId = approvalType.uniqueId || (Date.now().toString(36) + Math.random().toString(36).substr(2));
            approvalType._originalSelectedItems = null;
            approvalType.siteSelect = {
                isChanged: false,
                loading: false,
                isUserInactive: vm.user.status !== 2,
                response: null,
                link: siteCollectionLink,
                requestParams: {
                    status: 'active',
                    withAccess: userResponse.id,
                    excludeSiteApproverUser: userResponse.id,
                    excludeSiteApproverType: approvalType.key
                },
                selectedLink: userResponse.getLink('user-site-approver-sites'),
                selectedRequestParams: {
                    type: approvalType.key
                },
                responseKeyPath: 'sites',
                selectedItems: [],
                onSelectedItemsLoaded: function (items) {
                    if (approvalType._originalSelectedItems === null) {
                        approvalType._originalSelectedItems = [];
                    }

                    var existingHrefs = {};
                    approvalType._originalSelectedItems.forEach(function (item) {
                        existingHrefs[item.href] = true;
                    });

                    items.forEach(function (item) {
                        if (!existingHrefs[item.href]) {
                            approvalType._originalSelectedItems.push({
                                id: item.id,
                                href: item.href,
                                value: item.value,
                                hierarchy: item.hierarchy
                            });
                        }
                    });
                }
            };
        }

        function cancel(approvalType) {
            if (!approvalType.siteSelect || !approvalType.siteSelect.discardChanges) {
                return;
            }

            approvalType.siteSelect.discardChanges();
        }

        function update(approvalType) {
            const sitesWithoutAccess = approvalType.siteSelect.selectedItems
                .filter(site => site.hasAccess === false)
                .map(site => site.value)
                .sort((a, b) => a.localeCompare(b));

            siteAccessConfirmationModal.open({
                titleMessage: 'ARE_YOU_SURE_SHORT',
                bodyMessage: sitesWithoutAccess.length
                    ? 'SITE_APPROVER_UPDATE_CONFIRMATION_WITH_ACCESS_BODY'
                    : 'SITE_APPROVER_UPDATE_CONFIRMATION_BODY',
                secondaryText: 'CANCEL',
                primaryText: 'CONFIRM',
                primaryClass: 'btn-primary',
                forceDirtyConfirm: true,
                translateValues: {
                    userName: vm.user.name,
                    approvalType: $translate.instant(approvalType.title),
                    count: sitesWithoutAccess.length
                },
                sites: sitesWithoutAccess
            }).result.then(() => {
                proceedWithUpdate(approvalType);
            }).catch(() => {});
        }

        function proceedWithUpdate(approvalType) {
            const currentItems = approvalType.siteSelect.selectedItems || [];
            const originalItems = approvalType._originalSelectedItems || [];

            const originalHrefs = new Set(originalItems.map(item => item.href));
            const currentHrefs = new Set(currentItems.map(item => item.href));

            const added = currentItems
                .filter(item => !originalHrefs.has(item.href))
                .map(item => ({ id: item.id, href: item.href, hierarchy: item.hierarchy }));

            const removed = originalItems
                .filter(item => !currentHrefs.has(item.href))
                .map(item => ({ id: item.id, href: item.href, hierarchy: item.hierarchy }));

            const data = {
                approvalType: approvalType.key,
                added,
                removed
            };

            approvalType.siteSelect.loading = true;

            apiClient.update(userResponse.getLink('edit-user-site-approvers'), data).then(function (response) {
                if (response) {
                    messenger.success('SITE_APPROVER_TYPE_UPDATED');

                    // Reset so onSelectedItemsLoaded rebuilds _originalSelectedItems
                    // page-by-page as fresh data loads (supports pagination).
                    approvalType._originalSelectedItems = null;
                    approvalType.siteSelect.updateSelectedOptions([]);
                } else {
                    messenger.error('REQUEST_ERROR');
                }
            }).catch(function () {
                messenger.error('REQUEST_ERROR');
            }).finally(function () {
                approvalType.siteSelect.loading = false;
            });
        }

        function buildApprovalTypes() {
            const approvalTypes = [
                createApprovalType('reactiveJobApprovers', 'REACTIVE_JOB_APPROVERS'),
                createApprovalType('reactiveJobExtensionsApprovers', 'REACTIVE_JOB_EXTENSIONS_APPROVERS')
            ];

            if (modulesService.isEnabled('master_tasks_module')) {
                approvalTypes.push(createApprovalType('plannedJobApprovers', 'PLANNED_JOB_APPROVERS'));
                approvalTypes.push(createApprovalType('plannedJobExtensionsApprovers', 'PLANNED_JOB_EXTENSIONS_APPROVERS'));
            }

            // quotes_module_enabled setting not available yet - would be ready after merging feature/quotes-module - needs to remove: || true
            if (modulesService.isEnabled('quotes_module') || true) {
                approvalTypes.push(createApprovalType('quoteApprovers', 'QUOTE_APPROVERS'));
            }

            if (modulesService.isEnabled('contracts_module')) {
                approvalTypes.push(createApprovalType('contractApprovers', 'CONTRACT_APPROVERS'));
            }

            if (modulesService.isEnabled('surveys_module')) {
                approvalTypes.push(createApprovalType('surveyApprovers', 'SURVEY_APPROVERS'));
            }

            return approvalTypes;
        }

        function createApprovalType(key, title) {
            return {
                key,
                title,
                expanded: false,
                uniqueId: null,
                siteSelect: null
            };
        }

        let deregisterOnBeforeUnload = null;
        const windowOnbeforeunloadBck = $window.onbeforeunload;
        function onBeforeUnload(event) {
            const unsaved = [];

            for (const type of vm.approvalTypes) {
                if (type.siteSelect && type.siteSelect.isChanged) {
                    unsaved.push(type);
                }
            }

            if (unsaved.length) {
                const answer = confirm($translate.instant('NAVIGATION_CONFIRMATION'));

                if (!answer) {
                    event.preventDefault();
                    $timeout(() => $rootScope.$broadcast('$stateChangeAborted'), 0);

                    unsaved.forEach((item) => {
                        item.expanded = true;
                    });

                    const first = unsaved[0];
                    const $element = angular.element('.approval-type-' + first.key);
                    if ($element && $element.length) {
                        $element.parent()[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
                        $element.addClass('highlight');
                        $timeout(() => $element.removeClass('highlight'), 1000);
                    }

                    return false;
                }

                deregisterOnBeforeUnload && deregisterOnBeforeUnload();
                $window.onbeforeunload = windowOnbeforeunloadBck;
            } else {
                deregisterOnBeforeUnload && deregisterOnBeforeUnload();
                $window.onbeforeunload = windowOnbeforeunloadBck;
            }
        }

        deregisterOnBeforeUnload = $rootScope.$on('$stateChangeStart', onBeforeUnload);
        $window.onbeforeunload = onBeforeUnload;
    }
})();
